import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { User } from '../types'
import SignupScreen from './SignupScreen'
import PasswordResetScreen from './PasswordResetScreen'
import './LoginScreen.css'

interface LoginScreenProps {
  onLoginSuccess: (user: User) => void
}

type AuthMode = 'login' | 'signup' | 'reset'

export default function LoginScreen({ onLoginSuccess }: LoginScreenProps) {
  const [mode, setMode] = useState<AuthMode>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const { data: { user: authUser }, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (authError) throw authError

      if (authUser) {
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', authUser.id)
          .single()

        if (profileError) {
          // The Supabase session already exists at this point, so leaving it in
          // place strands the user on the login form with a raw Postgrest string
          // and no way out. Sign back out and say something useful.
          await supabase.auth.signOut()
          throw new Error(
            'Your sign-in worked but your DirectAuto profile could not be loaded. Ask an administrator to check your user record.'
          )
        }
        if (profile && profile.is_active === false) {
          // is_active was written by "Deactivate user" and read nowhere, so a
          // deactivated account kept signing in normally.
          await supabase.auth.signOut()
          throw new Error('This account has been deactivated. Contact an administrator.')
        }
        if (profile) {
          onLoginSuccess(profile)
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  if (mode === 'signup') {
    return <SignupScreen 
      onSignupSuccess={onLoginSuccess}
      onToggleLogin={() => setMode('login')}
    />
  }

  if (mode === 'reset') {
    return <PasswordResetScreen onBackToLogin={() => setMode('login')} />
  }

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-header">
          <h1>DirectAuto</h1>
          <p>Automotive Wholesale Platform</p>
        </div>

        <form onSubmit={handleLogin}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" disabled={loading} className="login-button">
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className="auth-links">
          <button 
            type="button"
            className="link-button"
            onClick={() => setMode('reset')}
          >
            Forgot password?
          </button>
          <span className="separator">•</span>
          <button 
            type="button"
            className="link-button"
            onClick={() => setMode('signup')}
          >
            Create account
          </button>
        </div>
      </div>
    </div>
  )
}
